/**
 * Workout builder session settings — mirrors mobile GarminWorkoutStructure meta.
 * Stored values in step targets are always percent indices (Garmin targetValueType=PERCENT).
 * The editor always uses ranges; legacy single-target readers remain compatible with equal bounds.
 */

import i18n from 'i18next';
import { FTP_PERCENT_MAX } from './workoutStructure';
import {
  formatBuilderIntensityLabel,
  isAbsoluteTargetMode,
} from './intensityMetricHelpers';

export type PrimaryLengthMetric = 'duration' | 'distance';
export type PrimaryIntensityMetric =
  | 'percentOfFtp'
  | 'percentOfMaxHr'
  | 'percentOfLTHR'
  | 'percentOfThresholdSpeed';
export type PrimaryIntensityTargetOrRange = 'range' | 'single';

export interface WorkoutBuilderSettings {
  primaryLengthMetric: PrimaryLengthMetric;
  primaryIntensityMetric: PrimaryIntensityMetric;
  primaryIntensityTargetOrRange: PrimaryIntensityTargetOrRange;
}

export const DEFAULT_WORKOUT_BUILDER_SETTINGS: WorkoutBuilderSettings = {
  primaryLengthMetric: 'duration',
  primaryIntensityMetric: 'percentOfFtp',
  primaryIntensityTargetOrRange: 'range',
};

export const LENGTH_METRIC_OPTIONS: { key: PrimaryLengthMetric; label: string }[] = [
  { key: 'duration', label: 'Duration' },
  { key: 'distance', label: 'Distance' },
];

export const INTENSITY_METRIC_OPTIONS: { key: PrimaryIntensityMetric; label: string }[] = [
  { key: 'percentOfFtp', label: '% FTP' },
  { key: 'percentOfMaxHr', label: '% Max HR' },
  { key: 'percentOfLTHR', label: '% LTHR' },
  { key: 'percentOfThresholdSpeed', label: '% Speed' },
];

export interface IntensityThresholds {
  ftp: number;
  maxHr: number;
  ltHr: number;
  /** Lactate-threshold running speed (m/s). */
  thresholdSpeedMps: number;
}

export function isRunningBuilderSettings(s: WorkoutBuilderSettings): boolean {
  return (
    s.primaryIntensityMetric === 'percentOfThresholdSpeed' ||
    s.primaryIntensityMetric === 'percentOfMaxHr' ||
    s.primaryIntensityMetric === 'percentOfLTHR' ||
    s.primaryLengthMetric === 'distance'
  );
}

export function parseWorkoutBuilderSettings(
  meta?: Partial<WorkoutBuilderSettings> | null,
): WorkoutBuilderSettings {
  if (!meta) return { ...DEFAULT_WORKOUT_BUILDER_SETTINGS };
  return {
    primaryLengthMetric:
      meta.primaryLengthMetric === 'distance' ? 'distance' : 'duration',
    primaryIntensityMetric:
      INTENSITY_METRIC_OPTIONS.some(o => o.key === meta.primaryIntensityMetric)
        ? (meta.primaryIntensityMetric as PrimaryIntensityMetric)
        : 'percentOfFtp',
    primaryIntensityTargetOrRange: 'range',
  };
}

export function settingsSummaryLabel(s: WorkoutBuilderSettings): string {
  const lenKey = s.primaryLengthMetric === 'duration' ? 'duration' : 'distance';
  const len = i18n.t(`workoutBuilder.setup.summary.${lenKey}`, {
    defaultValue: lenKey === 'duration' ? 'Duration' : 'Distance',
  });
  const intens = intensityMetricShortLabel(s.primaryIntensityMetric);
  const targKey = s.primaryIntensityTargetOrRange === 'range' ? 'range' : 'single';
  const targ = i18n.t(`workoutBuilder.setup.summary.${targKey}`, {
    defaultValue: targKey === 'range' ? 'Range' : 'Single',
  });
  return `${len} · ${intens} · ${targ}`;
}

export function intensityMetricShortLabel(metric: PrimaryIntensityMetric): string {
  const fallback =
    INTENSITY_METRIC_OPTIONS.find(o => o.key === metric)?.label ?? '% FTP';
  return i18n.t(`workoutBuilder.setup.intensity.${metric}`, { defaultValue: fallback });
}

export function chartTitleForMetric(metric: PrimaryIntensityMetric): string {
  switch (metric) {
    case 'percentOfMaxHr':
      return 'HR Profile (% Max HR)';
    case 'percentOfLTHR':
      return 'HR Profile (% LTHR)';
    case 'percentOfThresholdSpeed':
      return 'Pace Profile (% Speed)';
    default:
      return 'Power Profile (% FTP)';
  }
}

/** Max wheel/index for step intensity sliders per metric. */
export function intensityPercentMax(metric: PrimaryIntensityMetric): number {
  if (metric === 'percentOfFtp') return FTP_PERCENT_MAX;
  return metric === 'percentOfThresholdSpeed' ? 100 : 150;
}

/** Reference threshold label in totals strip (FTP, Max HR, …). */
export function referenceThresholdLabel(
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): { value: string; label: string } | null {
  switch (metric) {
    case 'percentOfFtp':
      return thresholds.ftp > 0
        ? { value: `${thresholds.ftp} W`, label: 'FTP' }
        : null;
    case 'percentOfMaxHr':
      return thresholds.maxHr > 0
        ? { value: `${thresholds.maxHr} bpm`, label: 'Max HR' }
        : null;
    case 'percentOfLTHR':
      return thresholds.ltHr > 0
        ? { value: `${thresholds.ltHr} bpm`, label: 'LTHR' }
        : null;
    case 'percentOfThresholdSpeed':
      return thresholds.thresholdSpeedMps > 0
        ? {
            value: formatPaceSeconds(1000 / thresholds.thresholdSpeedMps),
            label: 'Threshold pace',
          }
        : null;
    default:
      return null;
  }
}

function formatPaceSeconds(secPerKm: number): string {
  const total = Math.round(secPerKm);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Convert stored percent → absolute value for UI hint only. */
export function absoluteFromPercent(
  pct: number,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): number | null {
  switch (metric) {
    case 'percentOfFtp':
      return thresholds.ftp > 0 ? Math.round((pct / 100) * thresholds.ftp) : null;
    case 'percentOfMaxHr':
      return thresholds.maxHr > 0 ? Math.round((pct / 100) * thresholds.maxHr) : null;
    case 'percentOfLTHR':
      return thresholds.ltHr > 0 ? Math.round((pct / 100) * thresholds.ltHr) : null;
    case 'percentOfThresholdSpeed':
      return thresholds.thresholdSpeedMps > 0
        ? Math.round((pct / 100) * thresholds.thresholdSpeedMps * 100) / 100
        : null;
    default:
      return null;
  }
}

export function formatStepIntensityLine(
  min: number,
  max: number,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
  targetOrRange?: PrimaryIntensityTargetOrRange,
): string {
  if (isAbsoluteTargetMode(targetOrRange)) {
    return formatBuilderIntensityLabel(min, max, metric, thresholds, targetOrRange);
  }
  const unit = intensityMetricShortLabel(metric);
  const base = `${min}–${max} ${unit}`;
  const absMin = absoluteFromPercent(min, metric, thresholds);
  const absMax = absoluteFromPercent(max, metric, thresholds);
  if (metric === 'percentOfThresholdSpeed' && thresholds.thresholdSpeedMps > 0) {
    const paceSlow = formatPaceSeconds(1000 / ((min / 100) * thresholds.thresholdSpeedMps));
    const paceFast = formatPaceSeconds(1000 / ((max / 100) * thresholds.thresholdSpeedMps));
    return `${base} · ${paceFast}–${paceSlow} /km`;
  }
  if (absMin != null && absMax != null) {
    const suffix = metric === 'percentOfFtp' ? 'W' : 'bpm';
    return `${base} · ${absMin}–${absMax} ${suffix}`;
  }
  return base;
}
